/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import React, { CSSProperties, forwardRef, MouseEventHandler, useCallback } from "react";

import { useCosmosComponentContext } from "@components/context";
import { CosmosLinkType } from "@components/context/DefaultLink";
import { useCookie } from "@components/hooks";
import type { AnalyticEventProps } from "@components/types";
import { useEmitEventOnClick } from "@components/utils/analytics";
import { isLauncher, isSameOrigin, normalizeLocaleHref, safeToStringUrl } from "@cosmos/common-utils";
import { useLocale, useLocales } from "@epic-mw/i18n";
import { EPIC_LOCALE_COOKIE_NAME } from "@wf-components/navigation-consumer";
import { appendSetLangParam } from "./CosmosLink.utils";

export interface CosmosLinkProps extends CosmosLinkType {
	className?: string;
	style?: CSSProperties;
	children?: React.ReactNode;
	analytics?: AnalyticEventProps;
	disabled?: boolean;
	title?: string;
	target?: string;
	// TODO: discuss this error;
	href: string | CosmosLinkType["href"];
	prefetch?: boolean;
	/** Navigate to another app without client routing or locale path rewriting. */
	reloadDocument?: boolean;
	/** ARIA role and state for the rendered element, for example a tab button. */
	role?: React.AriaRole;
	id?: string;
	tabIndex?: number;
	"aria-selected"?: boolean;
	"aria-controls"?: string;
	"aria-label"?: string;
}

interface CosmosLinkButtonProps extends Omit<CosmosLinkProps, "onClick" | "href"> {
	onClick?: MouseEventHandler<HTMLButtonElement> | undefined;
}

const CosmosLinkButton = ({
	children,
	analytics,
	className,
	style,
	disabled,
	onClick: originalOnClick,
	title,
	role,
	id,
	"aria-selected": ariaSelected,
	"aria-controls": ariaControls,
	"aria-label": ariaLabel,
}: CosmosLinkButtonProps): React.JSX.Element => {
	const emitEvent = useEmitEventOnClick(analytics);
	const onClick: MouseEventHandler<HTMLButtonElement> = useCallback(
		(e) => {
			emitEvent(e);

			if (originalOnClick && typeof originalOnClick === "function") {
				originalOnClick(e);
			}
		},
		[originalOnClick, emitEvent],
	);

	return (
		<button
			className={className}
			style={style}
			onClick={onClick}
			disabled={disabled}
			title={title}
			tabIndex={!originalOnClick ? -1 : undefined}
			role={role}
			id={id}
			aria-selected={ariaSelected}
			aria-controls={ariaControls}
			aria-label={ariaLabel}
		>
			{children}
		</button>
	);
};

export const CosmosLink = forwardRef(
	(
		{ children, analytics, disabled, reloadDocument, ...props }: CosmosLinkProps,
		forwardedRef: React.Ref<HTMLAnchorElement>,
	): React.JSX.Element => {
		const { Link } = useCosmosComponentContext();
		const emitEvent = useEmitEventOnClick(analytics);
		const locale = useLocale();
		const locales = useLocales();
		const epicLocaleCookie = useCookie(EPIC_LOCALE_COOKIE_NAME);

		const originalOnClick = props.onClick as MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;

		const onClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
			(e) => {
				emitEvent(e);

				if (originalOnClick && typeof originalOnClick === "function") {
					originalOnClick(e);
				}
			},
			[originalOnClick, emitEvent],
		);

		if (!props.href) {
			return (
				<CosmosLinkButton
					analytics={analytics}
					onClick={originalOnClick}
					className={props.className}
					style={props.style}
					disabled={disabled}
					title={props?.title}
					target={props?.target}
					role={props.role}
					id={props.id}
					aria-selected={props["aria-selected"]}
					aria-controls={props["aria-controls"]}
					aria-label={props["aria-label"]}
				>
					{children}
				</CosmosLinkButton>
			);
		}

		// Handle launcher URLs: treat as Epic (same-origin), use href as-is, target="_self"
		const hrefString = typeof props.href === "string" ? props.href : safeToStringUrl(props.href);
		if (isLauncher(hrefString)) {
			const finalTarget = props.target || "_self";
			const rel = finalTarget === "_blank" ? "noopener noreferrer" : "";
			const finalHref = props.href; // Use href as-is without modifications

			return (
				<Link {...props} href={finalHref} target={finalTarget || ""} rel={rel} onClick={onClick} ref={forwardedRef}>
					{children}
				</Link>
			);
		}

		const isEpic = isSameOrigin(hrefString);
		const finalTarget = props.target || (isEpic ? "_self" : "_blank");
		const rel = finalTarget === "_blank" ? "noopener noreferrer" : "";

		// Normalize locale href for string paths
		let finalHref: string | CosmosLinkType["href"] = props.href;
		if (typeof finalHref === "string") {
			if (!reloadDocument) {
				finalHref = normalizeLocaleHref(finalHref, locale, locales);
			}
			if (epicLocaleCookie) {
				const currentHostname = typeof window !== "undefined" ? window.location.hostname : "";
				finalHref = appendSetLangParam(finalHref, epicLocaleCookie, locales, currentHostname);
			}
		}

		if (reloadDocument) {
			const anchorProps = { ...props };
			delete anchorProps.prefetch;
			return (
				<a
					{...anchorProps}
					href={safeToStringUrl(finalHref)}
					target={finalTarget}
					rel={rel}
					onClick={onClick}
					ref={forwardedRef}
				>
					{children}
				</a>
			);
		}

		return (
			<Link {...props} href={finalHref} target={finalTarget || ""} rel={rel} onClick={onClick} ref={forwardedRef}>
				{children}
			</Link>
		);
	},
);

CosmosLink.displayName = "CosmosLink";
